import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cc from 'classcat';
import { EIconSize } from 'utils/constants/app/ui';
import { CLOSED_MARKETS } from 'utils/constants/sportsbook/closed-markets-list';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { ADD_MARKET_CARDS } from 'utils/constants/tour';
import { TOUR_ACTIVE_CLASS } from 'utils/generic/tour-settings';
import { isMobile } from 'utils/is-mobile';
import { Market } from 'interfaces/sportsbook-data-levels';
import {
  Body,
  EachWayContainer,
  Extra,
  Header,
  New,
  TitleContainer
} from './style';
import { ConditionalWrapper } from 'components/ConditionalWrapper';
import { EventAction } from 'components/EventAction';
import { GlobalIcon } from 'components/GlobalIcon';
import { getEachWayPartnerTerms } from 'store/selectors/racing-data';
import './index.less';

type PanelProps = {
  title: string;
  children: any;
  groupKey: string;
  image?: any;
  icon?: any;
  extra?: any;
  extraMarketInfo?: ReactNode;
  market?: Market[];
  Index?: number;
  extraFavourite?: ReactElement;
  notCompact?: boolean;
  toggleCompact?: (notCompact: boolean) => void;
  allCollapsed?: boolean;
  isFavorite?: boolean;
};

const getIsActive = ({
  groupKey,
  allCollapsed,
  notCompact,
  Index
}: {
  groupKey: string;
  allCollapsed: boolean | undefined;
  notCompact: boolean | undefined;
  Index: number | undefined;
}) => {
  const { exacts, parts, regExps } = CLOSED_MARKETS;

  if (notCompact !== undefined) {
    return notCompact;
  }

  if (allCollapsed !== undefined) {
    return !allCollapsed;
  }

  if (!+SpringConfigs.SPORT_MARKET_IS_COMPACT) {
    return true;
  }

  if (+SpringConfigs.SPORT_MARKET_IS_COMPACT) {
    return (Index || 0) < +SpringConfigs.SPORT_MARKET_IS_COMPACT;
  }

  if (exacts[groupKey]) {
    return false;
  }

  for (const part of parts) {
    if (groupKey && groupKey.includes(part)) {
      return false;
    }
  }

  for (const regExp of regExps) {
    if (regExp.test(groupKey)) {
      return false;
    }
  }

  return false;
};

export const MarketCollapse = (props: PanelProps): ReactElement => {
  const [active, setActive] = useState(
    getIsActive({
      groupKey: props.groupKey,
      allCollapsed: props.allCollapsed,
      notCompact: props.notCompact,
      Index: props.Index
    })
  );

  const [isNew, setIsNew] = useState(false);
  const { t } = useTranslation();
  const manualEachWayTerms = useSelector(getEachWayPartnerTerms);

  useEffect(() => {
    setActive(
      getIsActive({
        groupKey: props.groupKey,
        allCollapsed: props.allCollapsed,
        notCompact: props.notCompact,
        Index: props.Index
      })
    );
  }, [props?.Index, props.allCollapsed, props.groupKey, props.notCompact]);

  useEffect(() => {
    (props.market || []).forEach(element => {
      if (element.is_new) {
        setIsNew(true);
      }
    });
  }, [props.market]);

  const eachWay = useMemo(() => {
    const market = props.market?.length ? props.market[0] : null;
    const eachWay: {
      allowed: boolean;
      K: number;
      place: number;
    } = {
      allowed: false,
      K: 0,
      place: 0
    };

    if (market) {
      const eachWayData = manualEachWayTerms[String(market.id)]
        ? {
            allowed: manualEachWayTerms[String(market.id)],
            K: manualEachWayTerms[String(market.id)].ManualEachWayK,
            place: manualEachWayTerms[String(market.id)].ManualEachWayPlace
          }
        : {
            allowed: market.extra_info,
            K: market.extra_info?.EachWayK,
            place: market.extra_info?.EachWayPlace
          };

      eachWay.allowed = !!(
        eachWayData.allowed &&
        eachWayData.K &&
        eachWayData.place
      );

      if (eachWay.allowed) {
        eachWay.K = eachWayData.K || 0;
        eachWay.place = eachWayData.place || 0;
      }
    }

    return eachWay;
  }, [props.market, manualEachWayTerms]);

  const hasEarlyPayout = !!props?.market?.[0]?.has_early_payout;

  return (
    <ConditionalWrapper
      condition={Boolean(
        active &&
          props.Index === 0 &&
          SpringConfigs.USER_PERSONALIZATION_ENABLED
      )}
      wrapper={children => (
        <div
          className={cc([
            { [`${TOUR_ACTIVE_CLASS}--market`]: active && props.Index === 0 }
          ])}
          {...(SpringConfigs.USER_PERSONALIZATION_ENABLED &&
          active &&
          props.Index === 0
            ? { 'data-tour': ADD_MARKET_CARDS }
            : {})}
        >
          {children}
        </div>
      )}
    >
      <Header
        data-testid="marketHeader"
        $isActive={active}
        onClick={() => {
          setActive(!active);

          props.toggleCompact && props.toggleCompact(!active);
        }}
        className={cc([
          'market-collapse',
          {
            marketCollapse__earlyPayout: hasEarlyPayout,
            'marketCollapse__earlyPayout--active': hasEarlyPayout && active,
            marketCollapse__favorite:
              SpringConfigs.USER_PERSONALIZATION_ENABLED && props.isFavorite,
            'marketCollapse__favorite--active':
              SpringConfigs.USER_PERSONALIZATION_ENABLED &&
              props.isFavorite &&
              active
          }
        ])}
      >
        <TitleContainer
          className={!isMobile() ? '' : 'title_container--mobile'}
        >
          <span style={{ overflow: isMobile() ? 'hidden' : '' }}>
            {props.title}
          </span>
          {props.extraMarketInfo ? props.extraMarketInfo : null}
          {isNew && <New>{t('account.new')}</New>}
        </TitleContainer>
        {eachWay.allowed && (
          <EachWayContainer>
            {t('sportsbook.eachWayInfo', {
              K: eachWay.K,
              place: eachWay.place
            })}
          </EachWayContainer>
        )}
        <Extra
          onClick={event => {
            event.stopPropagation();
          }}
          className="marketCollapse__tooltip-inner"
        >
          {props.extra}
        </Extra>
        {props.extraFavourite}
        <EventAction
          size="sm"
          ghost
          icon={
            <GlobalIcon
              lib="generic"
              name="caretDownSmall"
              theme="default"
              size={EIconSize._24}
              className={cc([
                'marketCollapse__caret',
                {
                  'marketCollapse__caret-expanded': active
                }
              ])}
            />
          }
        />
      </Header>
      {active && (
        <Body
          data-testid="marketBody"
          className={cc([
            'markets__body-container',
            {
              marketBody__earlyPayout: hasEarlyPayout,
              'markets__body-container__favorite':
                SpringConfigs.USER_PERSONALIZATION_ENABLED && props.isFavorite
            }
          ])}
        >
          {props.children}
        </Body>
      )}
    </ConditionalWrapper>
  );
};
